/**
 * The amenity & policy pop-out — the RFP's thirteen questions, in one modal.
 *
 * Jeff, 2026-09-10: "this is what we'd need for the pop-outs… when adding a new
 * contract, all the amenity questions should show up."
 *
 * ── A PURE EDITOR, deliberately ─────────────────────────────────────────────
 * Every other modal in this feature writes to the API itself. This one does not,
 * because it has two hosts with two different write moments: on the create form
 * the answers ride along in the POST that creates the agreement (there is no
 * agreement to PATCH yet), and in the drawer they are part of an edit. So it
 * takes answers in, hands answers back, and knows nothing about either. The host
 * owns the write.
 *
 * ── The draft is local, and Cancel means cancel ─────────────────────────────
 * Thirteen questions is a long modal, several of them free text. Editing the
 * host's state directly would mean a mis-click on the backdrop keeps half an
 * answer — and worse, a Cancel that visibly changes nothing while having already
 * changed everything. So the modal edits its own copy and `onSave` is the only
 * way anything leaves it.
 *
 * ── Two questions appear and disappear ──────────────────────────────────────
 * "If yes, provide dates" and "if no, include fee details" are only asked when
 * the answer they depend on has been given. A hidden answer stays in the draft —
 * losing two paragraphs of renovation detail to a mis-click on the yes/no above
 * it would be its own defect — and `toPayload` is what refuses to store it.
 */
import { useState } from 'react';
import { Modal } from '@/features/crm/components/Modal';
import {
  AMENITY_QUESTIONS,
  answeredCount,
  askedCount,
  isAsked,
  type AmenityAnswers,
} from '@/features/hotel-supplier/amenities';

export function AmenitiesModal({
  answers,
  onClose,
  onSave,
  testId,
  title = 'Amenities & policies',
}: {
  answers: AmenityAnswers;
  onClose: () => void;
  onSave: (next: AmenityAnswers) => void;
  testId: string;
  title?: string;
}) {
  const [draft, setDraft] = useState<AmenityAnswers>(answers);

  const set = (key: string, value: string) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(draft);
  };

  const asked = askedCount(draft);
  const done = answeredCount(draft);

  return (
    <Modal
      actions={
        <>
          <button
            className="btn btn-outline"
            data-testid={`${testId}-cancel`}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          {/*
           * A plain button calling `onSave`, NOT `type="submit"` with a `form`
           * attribute. The action row lives outside the <form> (the Modal owns
           * that layout), so cross-form submission would depend on the `form`
           * attribute being honoured — which browsers do and jsdom does not
           * reliably, meaning the whole save path would be untestable headless.
           * The <form>'s own onSubmit still handles Enter from any field.
           */}
          <button
            className="btn btn-primary"
            data-testid={`${testId}-save`}
            onClick={() => onSave(draft)}
            type="button"
          >
            Save answers
          </button>
        </>
      }
      onClose={onClose}
      size="wide"
      testId={testId}
      title={title}
    >
      <form
        data-testid={`${testId}-form`}
        id={`${testId}-form`}
        onSubmit={submit}
      >
        <p className="page-intro" style={{ marginTop: 0 }}>
          {/*
           * The count is the point of the summary. These are questions a hotel
           * answers once, and "which ones are still open?" is the only thing
           * anyone wants to know on reopening this.
           */}
          <span data-testid={`${testId}-count`}>
            {done} of {asked} answered
          </span>
          . Anything left blank is recorded as unanswered rather than as a no.
        </p>

        <AmenityQuestionFields answers={draft} set={set} testId={testId} />
      </form>
    </Modal>
  );
}

/**
 * The questions themselves, without a modal around them.
 *
 * Exported because the two hosts wrap them differently and must not each own a
 * copy. On the create page the questions are a POP-OUT from a form that is
 * already twenty fields long — Jeff's "pop-outs". In the drawer's edit modal
 * they are INLINE, because that surface is itself a modal and `Modal` states
 * plainly that it supports one at a time and keeps no stack: two would fight
 * over the focus trap and both would close on one Escape.
 */
export function AmenityQuestionFields({
  answers,
  set,
  testId,
}: {
  answers: AmenityAnswers;
  set: (key: string, value: string) => void;
  testId: string;
}) {
  return (
    <div
      /*
       * TWO COLUMNS where there is room, and `auto-fit` rather than a fixed
       * count so a narrow viewport collapses to one without a breakpoint. The
       * long conditional answers span both — a three-line textarea in a half
       * column is narrower than the sentence it holds.
       */
      style={{
        display: 'grid',
        gap: 14,
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      }}
    >
      {AMENITY_QUESTIONS.filter((q) => isAsked(q, answers)).map((q) => {
        const id = `${testId}-${q.key}`;
        return (
          <div
            data-testid={`${testId}-field-${q.key}`}
            key={q.key}
            /*
             * Two placements, both for legibility rather than decoration.
             *
             * The long answers span BOTH columns: "which outlets, alcohol,
             * room service, blackout times" and "dates, floors or facilities
             * affected" are sentences, and a three-row textarea at half width
             * is narrower than the prompt asking for them.
             *
             * The conditional questions are indented, so "Fee details" reads
             * as belonging to the fee question it followed rather than as a
             * fourteenth independent question.
             */
            style={{
              ...(q.control === 'textarea' ? { gridColumn: '1 / -1' } : null),
              ...(q.showWhen ? { paddingLeft: 16 } : null),
            }}
          >
            <label className="field-label" htmlFor={id}>
              {q.label}
            </label>
            {q.control === 'select' ? (
              <select
                className="input"
                data-testid={id}
                id={id}
                onChange={(e) => set(q.key, e.target.value)}
                value={answers[q.key] ?? ''}
              >
                {/*
                 * An explicit empty option, and it is the default. Without
                 * one, a `<select>` shows its first real option and the
                 * form silently asserts an answer nobody gave — which for
                 * "are rates available to contractors" is a commitment.
                 */}
                <option value="">— not answered —</option>
                {q.options!.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : q.control === 'textarea' ? (
              <textarea
                className="textarea"
                data-testid={id}
                id={id}
                onChange={(e) => set(q.key, e.target.value)}
                placeholder={q.placeholder}
                rows={3}
                value={answers[q.key] ?? ''}
              />
            ) : (
              <input
                className="input"
                data-testid={id}
                id={id}
                onChange={(e) => set(q.key, e.target.value)}
                placeholder={q.placeholder}
                type="text"
                value={answers[q.key] ?? ''}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
